// Client-side memory of this device's bookings for one studio. The access
// token is the only proof of ownership: it is generated here, sent once with
// the idempotency key and stored only on this device (the server keeps a hash).

export interface StoredBooking {
  token: string;
  id: string;
  code: string;
  startsAt: string;
  serviceName: string;
  savedAt: string;
}

export interface PendingBooking {
  idempotencyKey: string;
  token: string;
  fingerprint: string;
  createdAt: number;
}

const key = (slug: string, kind: 'bookings' | 'pending') => `sgx:${slug}:${kind}`;

function read<T>(k: string, fallback: T): T {
  try {
    const v = localStorage.getItem(k);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}
function write(k: string, v: unknown) {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    // storage unavailable (private mode): the booking still exists server-side
  }
}

export function newAccessToken() {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  let s = '';
  bytes.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export const bookingStore = {
  list(slug: string): StoredBooking[] {
    return read<StoredBooking[]>(key(slug, 'bookings'), []).sort((a, b) => b.startsAt.localeCompare(a.startsAt));
  },
  save(slug: string, b: StoredBooking) {
    const all = read<StoredBooking[]>(key(slug, 'bookings'), []).filter((x) => x.id !== b.id);
    all.push(b);
    write(key(slug, 'bookings'), all.slice(-20));
  },
  remove(slug: string, id: string) {
    write(
      key(slug, 'bookings'),
      read<StoredBooking[]>(key(slug, 'bookings'), []).filter((x) => x.id !== id),
    );
  },
  /**
   * The same form submitted again (double tap, retry after a network error,
   * reload mid-request) reuses the same key + token, so the server returns the
   * existing booking instead of creating a copy.
   */
  pendingFor(slug: string, fingerprint: string): PendingBooking {
    const p = read<PendingBooking | null>(key(slug, 'pending'), null);
    if (p && p.fingerprint === fingerprint && Date.now() - p.createdAt < 30 * 60e3) return p;
    const fresh = { idempotencyKey: crypto.randomUUID(), token: newAccessToken(), fingerprint, createdAt: Date.now() };
    write(key(slug, 'pending'), fresh);
    return fresh;
  },
  clearPending(slug: string) {
    try {
      localStorage.removeItem(key(slug, 'pending'));
    } catch {
      /* ignore */
    }
  },
};
